// Quo messaging — Messages → Settings → Messaging provider.
//
// One page to choose HOW direct-member texts travel: Quo (formerly OpenPhone)
// or a per-host Twilio number. Exactly one provider is active. The Quo card
// is a two-step wizard (paste API key → validate → pick ONE inbox → Connect)
// that becomes a status card once connected (masked key, inbox, webhook
// state, history-sync progress, last event, Re-sync, Disconnect).
//
// Server contract: server/routes/quo-connection.ts. Gated on `quo_messaging`,
// which is generally available (shared/beta-ga.ts, backend plan D22 — Quo is a
// built-in feature), so every account passes; the not-available card below
// only shows if the access lookup itself says no.
//
// The Slack alerts card (components/messages/slack-alerts-settings.tsx) is
// independent of the Quo gate and renders in both branches.

import { useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRight,
  Check,
  ExternalLink,
  Loader2,
  MessageSquare,
  Phone,
  Plug,
  RefreshCw,
  ShieldCheck,
  Unplug,
} from "lucide-react";
import { format } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";
import { useBetaAccessState } from "@/hooks/use-beta-access";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { SlackAlertsSettings } from "@/components/messages/slack-alerts-settings";

type MessagingProvider = "none" | "twilio" | "quo";

type QuoBackfill = {
  status: "idle" | "pending" | "running" | "done" | "failed";
  conversationsTotal: number;
  conversationsMatched: number;
  imported: number;
  skipped: number;
  startedAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
};

type QuoProjection = {
  id: number;
  status: "active" | "disconnected" | "error";
  hasApiKey: boolean;
  apiKeyLast4: string | null;
  maskedApiKey: string | null;
  phoneNumberId: string | null;
  phoneNumber: string | null;
  phoneNumberName: string | null;
  webhook: { registered: boolean; id: string | null; url: string };
  lastError: string | null;
  eventsReceived: number;
  signatureFailures: number;
  lastEventAt: string | null;
  lastPolledAt: string | null;
  backfill: QuoBackfill;
};

type ProviderState = {
  provider: MessagingProvider;
  twilio: { phoneNumber: string | null };
  quo: QuoProjection | null;
};

type QuoInbox = {
  id: string;
  number: string;
  name: string | null;
  formattedNumber: string | null;
  userCount: number;
};

const PROVIDER_QUERY_KEY = ["/api/app/messaging-provider"] as const;
// Where a Quo workspace owner/admin generates an API key.
export const QUO_API_SETTINGS_URL = "https://my.quo.com/settings/api";

function formatWhen(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "PPp");
}

export default function AppMessagesSettings() {
  usePageTitle("Messaging Provider");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const beta = useBetaAccessState("quo_messaging");

  const [apiKey, setApiKey] = useState("");
  const [inboxes, setInboxes] = useState<QuoInbox[] | null>(null);
  const [selectedInboxId, setSelectedInboxId] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const providerQuery = useQuery<ProviderState>({
    queryKey: PROVIDER_QUERY_KEY,
    enabled: beta.granted,
    refetchInterval: (query) => {
      const status = query.state.data?.quo?.backfill?.status;
      return status === "pending" || status === "running" ? 3000 : false;
    },
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: PROVIDER_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["/api/app/quo-connection"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/account/messaging"] });
  };

  const validateMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await apiRequest("POST", "/api/app/quo-connection/validate", { apiKey: key });
      return (await res.json()) as { phoneNumbers: QuoInbox[] };
    },
    meta: { silentSave: true },
    onSuccess: (data) => {
      setInboxes(data.phoneNumbers);
      setSelectedInboxId(data.phoneNumbers.length === 1 ? data.phoneNumbers[0].id : null);
    },
    onError: (err: unknown) => {
      setInboxes(null);
      toast({
        title: "Couldn't verify the Quo API key",
        description: parseErrorMessage(err, "Quo did not accept the key."),
        variant: "destructive",
      });
    },
  });

  const connectMutation = useMutation({
    mutationFn: async (input: { apiKey: string; phoneNumberId: string }) => {
      const res = await apiRequest("POST", "/api/app/quo-connection", input);
      return (await res.json()) as QuoProjection;
    },
    onSuccess: (data) => {
      invalidateAll();
      setApiKey("");
      setInboxes(null);
      setSelectedInboxId(null);
      toast({
        title: "Quo connected",
        description: data?.phoneNumber
          ? `Member texts now send from ${data.phoneNumber}. Importing your conversation history…`
          : "Importing your conversation history…",
      });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't connect Quo",
        description: parseErrorMessage(err, "Quo refused the connection."),
        variant: "destructive",
      });
    },
  });

  const resyncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/quo-connection/resync");
      return res.json();
    },
    meta: { silentSave: true },
    onSuccess: () => {
      invalidateAll();
      toast({ title: "History sync started" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't start the history sync",
        description: parseErrorMessage(err, "Try again in a moment."),
        variant: "destructive",
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/app/quo-connection");
      return (await res.json()) as ProviderState;
    },
    onSuccess: () => {
      invalidateAll();
      setConfirmDisconnect(false);
      toast({ title: "Quo disconnected" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't disconnect Quo",
        description: parseErrorMessage(err, "Try again in a moment."),
        variant: "destructive",
      });
    },
  });

  const selectProviderMutation = useMutation({
    mutationFn: async (provider: MessagingProvider) => {
      const res = await apiRequest("PATCH", "/api/app/messaging-provider", { provider });
      return (await res.json()) as ProviderState;
    },
    onSuccess: (data) => {
      invalidateAll();
      toast({ title: data.provider === "twilio" ? "Twilio is now your messaging provider" : "Quo is now your messaging provider" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't switch provider",
        description: parseErrorMessage(err, "Connect the provider first."),
        variant: "destructive",
      });
    },
  });

  const state = providerQuery.data;
  const provider: MessagingProvider = state?.provider ?? "none";
  const quo = state?.quo && state.quo.status === "active" ? state.quo : null;
  const twilioNumber = state?.twilio?.phoneNumber ?? null;
  const backfill = quo?.backfill ?? null;
  const backfillBusy = backfill?.status === "pending" || backfill?.status === "running";
  const canConnectQuo = !twilioNumber;
  const anyPending = connectMutation.isPending || disconnectMutation.isPending;

  const header = (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="h-5 w-5 text-brand" />
          <h1
            className="text-xl font-semibold text-gray-900"
            data-testid="text-messaging-provider-heading"
            data-tour="messages-settings-heading"
          >
            Messaging provider
          </h1>
        </div>
        <p className="text-sm text-gray-500">
          Choose how texts to your direct-booked members are sent and received. PadSplit members are always
          messaged through PadSplit Messenger.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <ExplainerVideoButton pageKey="messages-settings" />
        <GuideButton pageKey="messages-settings" />
      </div>
    </div>
  );

  if (beta.loaded && !beta.granted) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto p-6">
          {header}
          <Card className="p-8 text-center" data-testid="card-messaging-provider-unavailable">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Not available on your account yet</h2>
            <p className="text-sm text-gray-600">
              The Quo messaging integration is rolling out gradually. Contact support@padpulse.io to request access.
            </p>
          </Card>
          <div className="mt-6">
            <SlackAlertsSettings />
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        {header}
        <GuideNudgeBanner pageKey="messages-settings" />

        {/* ── Provider chooser ─────────────────────────────────────────── */}
        <Card className="p-6 mb-6" data-tour="messages-settings-provider" data-testid="card-messaging-provider">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Active provider</h2>
          <p className="text-sm text-gray-600 mb-4">
            One provider at a time. Switch by connecting the other one.
          </p>
          {providerQuery.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div
                className={`rounded-lg border p-4 ${provider === "quo" ? "border-brand bg-brand/5" : "border-gray-200"}`}
                data-testid="card-provider-quo"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <Plug className="h-4 w-4 text-brand" />
                    Quo
                  </div>
                  {provider === "quo" ? (
                    <Badge data-testid="badge-provider-quo-active">Active</Badge>
                  ) : quo ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => selectProviderMutation.mutate("quo")}
                      disabled={selectProviderMutation.isPending}
                      data-testid="button-select-provider-quo"
                    >
                      Use Quo
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-gray-600">
                  Connect your Quo (OpenPhone) workspace. Member texts send from one Quo inbox and replies land in
                  Messages. {quo?.phoneNumber ? `Connected inbox: ${quo.phoneNumber}.` : "Not connected."}
                </p>
              </div>
              <div
                className={`rounded-lg border p-4 ${provider === "twilio" ? "border-brand bg-brand/5" : "border-gray-200"}`}
                data-testid="card-provider-twilio"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <Phone className="h-4 w-4 text-brand" />
                    Twilio number
                  </div>
                  {provider === "twilio" ? (
                    <Badge data-testid="badge-provider-twilio-active">Active</Badge>
                  ) : twilioNumber ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => selectProviderMutation.mutate("twilio")}
                      disabled={selectProviderMutation.isPending}
                      data-testid="button-select-provider-twilio"
                    >
                      Use Twilio
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-gray-600">
                  {twilioNumber
                    ? `A dedicated PadPulse number (${twilioNumber}).`
                    : "A dedicated PadPulse-provisioned number. Nothing set up yet."}
                </p>
                <Link
                  href="/app/account/messaging"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-brand underline"
                  data-testid="link-manage-twilio-number"
                >
                  Manage Twilio number <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          )}
        </Card>

        {/* ── Quo card: wizard or status ───────────────────────────────── */}
        <Card className="p-6 mb-6" data-tour="messages-settings-quo" data-testid="card-quo-connection">
          {providerQuery.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : quo ? (
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-brand" />
                    Quo connected
                  </h2>
                  <p className="text-sm text-gray-600">
                    Sending from{" "}
                    <span className="font-medium text-gray-900" data-testid="text-quo-inbox">
                      {quo.phoneNumberName ? `${quo.phoneNumberName} · ` : ""}
                      {quo.phoneNumber ?? quo.phoneNumberId}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => resyncMutation.mutate()}
                    disabled={resyncMutation.isPending || backfillBusy}
                    data-testid="button-quo-resync"
                  >
                    {resyncMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Re-sync history
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmDisconnect(true)}
                    disabled={disconnectMutation.isPending}
                    data-testid="button-quo-disconnect"
                  >
                    <Unplug className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                </div>
              </div>

              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-xs text-gray-500">API key</dt>
                  <dd className="font-mono text-gray-900" data-testid="text-quo-masked-key">
                    {quo.maskedApiKey ?? "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Inbound webhook</dt>
                  <dd className="text-gray-900" data-testid="text-quo-webhook">
                    {quo.webhook.registered ? (
                      <Badge variant="secondary">
                        <Check className="h-3 w-3 mr-1" />
                        Registered
                      </Badge>
                    ) : (
                      <Badge variant="destructive">Not registered</Badge>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Last message received</dt>
                  <dd className="text-gray-900" data-testid="text-quo-last-event">
                    {formatWhen(quo.lastEventAt)}
                    {quo.eventsReceived > 0 ? ` · ${quo.eventsReceived} total` : ""}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Last background check</dt>
                  <dd className="text-gray-900" data-testid="text-quo-last-poll">
                    {formatWhen(quo.lastPolledAt)}
                  </dd>
                </div>
              </dl>

              {backfill && (
                <div className="mt-4 rounded-md border border-gray-200 p-3 text-sm" data-testid="panel-quo-backfill">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-gray-900">History sync</div>
                    <Badge variant={backfill.status === "failed" ? "destructive" : "secondary"} data-testid="badge-quo-backfill-status">
                      {backfillBusy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      {backfill.status}
                    </Badge>
                  </div>
                  <div className="mt-1 text-xs text-gray-600" data-testid="text-quo-backfill-counts">
                    {backfill.conversationsTotal} conversations scanned · {backfill.conversationsMatched} matched members ·{" "}
                    {backfill.imported} messages imported
                    {backfill.finishedAt ? ` · finished ${formatWhen(backfill.finishedAt)}` : ""}
                  </div>
                  {backfill.lastError && (
                    <div className="mt-1 text-xs text-red-600" data-testid="text-quo-backfill-error">
                      {backfill.lastError}
                    </div>
                  )}
                </div>
              )}

              {quo.lastError && (
                <div className="mt-3 text-xs text-red-600" data-testid="text-quo-last-error">
                  {quo.lastError}
                </div>
              )}
            </div>
          ) : (
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Connect Quo</h2>
              <p className="text-sm text-gray-600 mb-3">
                Takes about two minutes. You need to be an owner or admin of your Quo workspace.
              </p>
              <ol className="mb-4 list-decimal space-y-2 pl-5 text-sm text-gray-700" data-testid="list-quo-setup-steps">
                <li>
                  Open{" "}
                  <a
                    href={QUO_API_SETTINGS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-brand underline"
                    data-testid="link-quo-api-settings"
                  >
                    Quo → Settings → API <ExternalLink className="h-3 w-3" />
                  </a>{" "}
                  and click <span className="font-medium">Generate API key</span>. Copy the key — Quo shows it only once.
                </li>
                <li>
                  Paste the key below and click <span className="font-medium">Validate key</span>. PadPulse checks it and
                  lists the phone numbers in your workspace.
                </li>
                <li>
                  Pick the inbox PadPulse should send from and click <span className="font-medium">Connect Quo</span>.
                  PadPulse registers a webhook with Quo so new texts, calls and call summaries flow into Messages, and
                  imports your existing conversations with any member whose phone number is on file.
                </li>
                <li>
                  To send texts to US numbers, your Quo workspace must have completed{" "}
                  <span className="font-medium">US carrier registration</span> (Quo → Settings → Trust Center). Call
                  summaries need Quo's Business or Scale plan.
                </li>
              </ol>
              {!canConnectQuo && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="text-quo-blocked-by-twilio">
                  A Twilio number ({twilioNumber}) is assigned to this account. Release it under Account → Messaging before
                  connecting Quo.
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Label htmlFor="quo-api-key" className="text-xs text-gray-600">
                    Quo API key
                  </Label>
                  <Input
                    id="quo-api-key"
                    type="password"
                    autoComplete="off"
                    placeholder="Paste your Quo API key"
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      setInboxes(null);
                      setSelectedInboxId(null);
                    }}
                    disabled={!canConnectQuo || anyPending}
                    data-testid="input-quo-api-key"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => validateMutation.mutate(apiKey.trim())}
                    disabled={!canConnectQuo || apiKey.trim().length < 8 || validateMutation.isPending || anyPending}
                    data-testid="button-quo-validate"
                  >
                    {validateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Validate key
                  </Button>
                </div>
              </div>

              {inboxes && (
                <div className="mt-5" data-testid="panel-quo-inboxes">
                  <div className="text-sm font-medium text-gray-900 mb-2">Choose the inbox to send from</div>
                  <div className="grid gap-2" role="radiogroup" aria-label="Quo inbox">
                    {inboxes.map((inbox) => {
                      const selected = inbox.id === selectedInboxId;
                      return (
                        <button
                          key={inbox.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => setSelectedInboxId(inbox.id)}
                          className={`flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${
                            selected ? "border-brand bg-brand/5" : "border-gray-200 hover:bg-gray-50"
                          }`}
                          data-testid={`radio-quo-inbox-${inbox.id}`}
                        >
                          <span>
                            <span className="font-medium text-gray-900">{inbox.name ?? "Inbox"}</span>
                            <span className="ml-2 text-gray-600">{inbox.formattedNumber ?? inbox.number}</span>
                          </span>
                          <span className="text-xs text-gray-500">
                            {inbox.userCount} {inbox.userCount === 1 ? "user" : "users"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4">
                    <Button
                      onClick={() =>
                        selectedInboxId && connectMutation.mutate({ apiKey: apiKey.trim(), phoneNumberId: selectedInboxId })
                      }
                      disabled={!selectedInboxId || connectMutation.isPending}
                      data-testid="button-quo-connect"
                    >
                      {connectMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plug className="h-4 w-4 mr-2" />
                      )}
                      Connect Quo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        <SlackAlertsSettings />

        <div className="text-sm text-gray-600">
          <Link
            href="/app/messages"
            className="text-brand underline inline-flex items-center gap-1"
            data-testid="link-back-to-messages"
          >
            Back to Messages <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <Dialog
        open={confirmDisconnect}
        onOpenChange={(open) => {
          if (!open && disconnectMutation.isPending) return;
          setConfirmDisconnect(open);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(e) => {
            if (disconnectMutation.isPending) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (disconnectMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (disconnectMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Disconnect Quo?</DialogTitle>
            <DialogDescription>
              Member texts will stop sending until you connect a provider again. Conversations already imported stay in
              Messages; new replies to your Quo number will no longer appear here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisconnect(false)} data-testid="button-cancel-quo-disconnect">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              data-testid="button-confirm-quo-disconnect"
            >
              {disconnectMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
